import { ENVIORNMENT } from '@/constants/config'

// Auction 直播間嵌入的 kit 是 shop-auction-live-chat-kit-frontend（live-chat-kit-frontend 的 shop 分叉）。
// `-shop` 後綴的網址是該分叉自己的部署（以 VITE_SYSTEM_TYPE=shop 建置，API 打 shop-api）；
// 沒有後綴的舊網址是 ecos 共用 kit（API 打 ecosmetics.live 體系），不要混用。
const LIVE_STREAM_KIT_EMBED_URL_DATA: Record<string, string> = {
  [ENVIORNMENT.DEV]: 'https://live-stream-kit-dev-shop.tutorabcshop.com/embed.js',
  [ENVIORNMENT.PRE]: 'https://live-stream-kit-pre-shop.tutorabcshop.com/embed.js',
  [ENVIORNMENT.PROD]: 'https://live-stream-kit-shop.tutorabcshop.com/embed.js',
}

// 本機要嵌自己的 shop-auction-live-chat-kit-frontend 時，用 env 覆寫（不動其他環境）：
//   1. kit repo：pnpm build-dev && pnpm preview --port 4173
//   2. shop repo：LIVE_STREAM_KIT_EMBED_URL=http://localhost:4173/embed.js pnpm dev
// ponytail: 用 env 覆寫而不是加一組 local 環境常數，省掉 config 分支
export const LIVE_STREAM_KIT_EMBED_URL =
  process.env.LIVE_STREAM_KIT_EMBED_URL ||
  LIVE_STREAM_KIT_EMBED_URL_DATA[`${process.env.DEPLOY_ENV}`] ||
  LIVE_STREAM_KIT_EMBED_URL_DATA[ENVIORNMENT.DEV]

// kit 掛載容器的固定 id（kit 的 initMyRoot / embed.js 以此 id 尋找容器，一頁僅能有一個）
export const LIVE_STREAM_KIT_CONTAINER_ID = 'live-stream-kit-root'

// kit 的角色參數（對齊 shop-auction-live-chat-kit-frontend src/constants/role.ts 的數值）
export const KIT_ROLE = {
  STUDENT: '10', // 已登入買家（需要有效 token）
  HOST: '20', // 主播 / 賣家
  IT: '30', // 後台人員
  GUEST: '90', // 未登入買家（kit 會自行取得 guest token）
}

// 需要登入才能進入直播間的角色（後台身分）。注意 shop-api 才是真正決定房間角色的一方，
// 這裡只擋「未登入卻用 ?role=20/30 進來」這種情況。
export const KIT_ROLES_REQUIRE_LOGIN = [KIT_ROLE.HOST, KIT_ROLE.IT]

// kit 在訪客想留言／出價時，會用 window CustomEvent 通知宿主頁跳登入。
// 事件名 = kit 的 bridge prefix（buyer-livestream）+ ':' + 事件字串，
// 見 kit 的 src/pages/LiveRoom/context/liveRoomContext.tsx 與 utils/customEventBridge.ts。
export const KIT_EVENT_CHECK_BUYER_STATUS = 'buyer-livestream:parent:auction:check-buyer-status'

// kit mobile 的購物袋只負責發事件，商品清單由宿主頁自己畫。
// 事件名沿用 kit context 既有的 handleOpenProductListDrawer（CornerImage、RoomCover 也發同一條），
// 不另立新事件；完整名稱 = bridge prefix（buyer-livestream）+ ':' + 'open-product-list-drawer'。
export const KIT_EVENT_OPEN_PRODUCT_LIST = 'buyer-livestream:open-product-list-drawer'

// kit 把 auction socket（sid=27）收到的原始訊息原封不動轉發到這個事件，detail 是 JSON 字串。
// 見 kit 的 src/pages/LiveRoom/index.tsx 的 handleAuctionMessage。
export const KIT_EVENT_CHILD_TO_PARENT = 'buyer-livestream:child-to-parent'

// 宿主頁可以驅動 kit 的兩個動作（kit 端的 listener 見 LiveRoom/index.tsx 與 Auction/BidButton）。
// bid:trigger 沒有 payload，它是叫 kit 自己的 BidButton 執行點擊，所以出價對象一定是 kit 當前那一場。
// 開拍：帶 sku_id 請 kit 跑 shop 的三步流程（預扣 → 用回傳 params 發 ssid=1 → res=0 才取消預扣）。
// kit 端 listener 在 pages/LiveRoom/index.tsx，實作在 methods/auction/startShopAuction。
// 注意這條掛在 live-stream-control bridge（跟 bid:trigger 的 buyer-livestream 不同）。
export const KIT_EVENT_AUCTION_OPEN_SESSION = 'live-stream-control:parent:auction:open-session'
// 取消整場：帶 auc_id 請 kit 發 ssid=8（kit 端 listener 同樣在 pages/LiveRoom/index.tsx）。
// auc_id 從 ssid=6/9 的狀態廣播拿（見 useAuctionLiveState），所以只有場次真的開起來之後才按得下去。
export const KIT_EVENT_AUCTION_CANCEL = 'live-stream-control:parent:auction:cancel'
export const KIT_EVENT_BID_TRIGGER = 'buyer-livestream:parent:bid:trigger'

// kit 的付款失敗 lightbox（Figma 689:22768）按「變更付款方式」時派這條，綁卡流程做在 shop 這邊。
// kit 只負責關掉自己的彈窗與發事件，見 kit 的 pages/LiveRoom/index.tsx。
export const KIT_EVENT_PAYMENT_CHANGE_METHOD = 'buyer-livestream:parent:payment:change-method'

// 買家按出價前，kit 會先查 shop-api 的付款卡與地址；缺任一項就派這條請宿主開「付款資訊」抽屜
// 讓買家補資料（設計稿 Figma 723:55414）。detail 是 { hasCard: boolean; hasAddress: boolean }，
// 由 kit 的 src/pages/LiveRoom/Auction/index.tsx handleBuyerButtonClick 派出。
export const KIT_EVENT_PAYMENT_INFO_REQUIRED = 'buyer-livestream:parent:payment:info-required'

// shop 房間狀態廣播（對齊 kit 的 WS_EVENT_SHOP_STATE）：置頂變更走這條，跟 auction 的 27 是兩條線。
// kit 的 origin socket 訊息已經原封不動轉到 KIT_EVENT_CHILD_TO_PARENT，宿主頁自己過濾即可。
export const KIT_SHOP_STATE_SID = 25
export const KIT_SHOP_STATE_SSID = {
  UNIVERSAL: 0, // SHOP_BYPASS，不寫進 tmc 歷史
  STATE: 1,
}
export const KIT_SHOP_STATE_TYPE_PIN = 'PIN'
// 定期廣播的房間狀態，content 帶 likes / views / top_products / pub_products
export const KIT_SHOP_STATE_TYPE_STATE = 'STATE'
// 付款結果（content.status：0 失敗 / 1 成功），對齊 kit 的 PayStatus。
// SDC-2607 起結標不再自動扣款，結標當下不會有這條，買家確認付款後才推；2（待付款）不再出現
export const KIT_SHOP_STATE_TYPE_PAY_STATUS = 'PAYSTATUS'

// 對齊 kit 的 constants/socketEvent.ts 的 WS_EVENT_AUCTION
export const KIT_AUCTION_SID = 27
export const KIT_AUCTION_SSID = {
  STATE: 6, // 對我方 getState 的 ack
  BROADCAST_STATE: 9, // 伺服器主動推播
}

// 拍品狀態（shop-api auction_lot.status）。注意 2 是「進行中」不是「已退回」——
// 最後一件被開拍預扣後 remaining 歸零，但它還在場上，清單仍要顯示。
export const AUCTION_LOT_STATUS = {
  /** 可標（quantity_remaining > 0） */
  AVAILABLE: 0,
  /** 無庫存可標（售完或退回） */
  UNAVAILABLE: 1,
  /** 進行中（最後一件在場上） */
  IN_PROGRESS: 2,
}

export const AUCTION_STATE = {
  NONE: 'none',
  CREATED: 'created',
  RUNNING: 'running',
}

// kit 也會把聊天 socket 的 CHANNEL 訊息整包轉發到 KIT_EVENT_CHILD_TO_PARENT，
// 房間狀態走 sid=7 / ssid=10，body 是 { key, value }。
// 對齊 kit 的 constants/socketEvent.ts（WS_EVENT_ROOM.sid=7、state=10、WS_EVENT_ROOM_STATES.userCount）。
export const KIT_ROOM_SID = 7
export const KIT_ROOM_SSID_STATE = 10
export const KIT_ROOM_STATE_KEY = {
  USER_COUNT: 'userCount', // 正在觀看人數
  ACTIVE: 'active', // 1 開播 / 0 結束
}
